import type { RecordModel } from 'pocketbase';
import type { TenantRole } from '@kioskforge/shared';
import { pb } from './pocketbase';
import { apiRequest } from './client';

export type TenantBranding = {
  accent?: string;
  accentInk?: string;
  logo?: string;
  name?: string;
  radius?: number;
};

export type TenantRecord = RecordModel & {
  name?: string;
  slug?: string;
  status?: string;
  device_limit?: number;
  timezone?: string;
  retention_days?: number;
  branding?: TenantBranding;
};

export type UserRecord = RecordModel & {
  name?: string;
  email?: string;
  role?: TenantRole;
  tenant?: string;
};

export async function getTenant(tenantId: string): Promise<TenantRecord> {
  return (await pb.collection('tenants').getOne(tenantId)) as TenantRecord;
}

export type TenantUsage = {
  screens: { used: number; max: number };
  devices: { used: number; max: number };
};

/** FB-0.2 / CC-8 — plan usage ("2 of 5 devices") against the tenant's limits. */
export async function fetchTenantUsage(): Promise<TenantUsage> {
  const body = await apiRequest<{ usage: TenantUsage }>('/tenant/usage');
  return body.usage;
}

export type TenantPatch = {
  name?: string;
  timezone?: string;
  branding?: TenantBranding;
  retention_days?: number;
};

/** Tenant writes are not permitted by PocketBase rules; they go through the server (§5). */
export async function updateTenant(patch: TenantPatch): Promise<void> {
  await apiRequest('/tenant', { method: 'PATCH', body: patch });
}

export async function listUsers(): Promise<UserRecord[]> {
  const records = await pb.collection('users').getFullList({ sort: 'created' });
  return records as UserRecord[];
}

export async function inviteUser(email: string, role: TenantRole, name?: string): Promise<void> {
  await apiRequest('/users/invite', { method: 'POST', body: { email, role, name } });
}

export async function changeRole(userId: string, role: TenantRole): Promise<void> {
  await apiRequest(`/users/${userId}/role`, { method: 'POST', body: { role } });
}

export async function removeUser(userId: string): Promise<void> {
  await apiRequest(`/users/${userId}`, { method: 'DELETE' });
}
